import { ERNEST_LOGO_BASE64 } from '@/constants/ernestLogo';
import { useUserProfile } from '@/hooks/useUserProfile';
import { supabase } from '@/lib/supabase';
import { LinearGradient } from 'expo-linear-gradient';
import * as Print from 'expo-print';
import { Stack, router, useLocalSearchParams } from 'expo-router';
import * as Sharing from 'expo-sharing';
import { ArrowLeft, DownloadSimple, FileText, PencilSimple, X } from 'phosphor-react-native';
import { useEffect, useState } from 'react';
import { Alert, Image, Platform, ScrollView, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import Markdown from 'react-native-markdown-display';
import { ActivityIndicator, Text } from 'react-native-paper';

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 1200;

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  purple600: '#7C3AED',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  emerald500: '#10B981',
};

interface NoteData {
  id: string;
  title: string | null;
  content: string | null;
  created_at: string | null;
}

// ✅ Alert adapté web/mobile
const showAlert = (title: string, message: string, buttons?: any[]) => {
  if (isWeb) {
    window.alert(`${title}\n\n${message}`);
    if (buttons && buttons.length === 1 && buttons[0].onPress) {
      buttons[0].onPress();
    }
  } else {
    Alert.alert(title, message, buttons);
  }
};

export default function NoteDetailScreen() {
  const { id } = useLocalSearchParams();
  const { profile } = useUserProfile();
  const [note, setNote] = useState<NoteData | null>(null);
  const [noteText, setNoteText] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchNote();
  }, [id]);

  // ✅ FONCTION AMÉLIORÉE : Extraction robuste du note_text depuis le JSON
  const extractNoteText = (content: string | null): string => {
    if (!content) return '';

    let cleanContent = content.trim();

    const decodeEscaped = (str: string): string => {
      return str
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\r')
        .replace(/\\t/g, '\t')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
    };

    const extractMarkdownFromString = (str: string): string => {
      if (str.startsWith('#') || str.startsWith('**') || str.startsWith('-') || str.startsWith('*') || str.startsWith('1.') || str.startsWith('>')) {
        return str;
      }

      if (str.startsWith('{')) {
        try {
          const innerParsed = JSON.parse(str);
          if (innerParsed && innerParsed.note_text && typeof innerParsed.note_text === 'string') {
            return extractMarkdownFromString(innerParsed.note_text);
          }
        } catch (e) {}

        const innerMatch = str.match(/"note_text"\s*:\s*"((?:[^"\\]|\\.)*)"/s);
        if (innerMatch && innerMatch[1]) {
          const decoded = decodeEscaped(innerMatch[1]);
          return extractMarkdownFromString(decoded);
        }
      }

      return str;
    };

    try {
      const parsed = JSON.parse(cleanContent);
      
      if (parsed && typeof parsed === 'object') {
        if (parsed.note_text && typeof parsed.note_text === 'string') {
          const noteTextValue = parsed.note_text;
          const extracted = extractMarkdownFromString(noteTextValue);
          if (extracted && !extracted.startsWith('{')) {
            return extracted;
          }
        }
        const textKeys = ['text', 'content', 'body', 'markdown', 'fiche'];
        for (const key of textKeys) {
          if (parsed[key] && typeof parsed[key] === 'string') {
            const extracted = extractMarkdownFromString(parsed[key]);
            if (extracted && !extracted.startsWith('{')) {
              return extracted;
            }
          }
        }
      }
    } catch (e) {}

    const noteTextRegex = /"note_text"\s*:\s*"((?:[^"\\]|\\.)*)"/s;
    const match = cleanContent.match(noteTextRegex);
    if (match && match[1]) {
      let extracted = decodeEscaped(match[1]);
      extracted = extractMarkdownFromString(extracted);
      if (extracted && !extracted.startsWith('{')) {
        return extracted;
      }
    }

    const markdownStartRegex = /\\?"#\s+[A-Z]/;
    const mdMatch = cleanContent.match(markdownStartRegex);
    if (mdMatch && mdMatch.index !== undefined) {
      let startIdx = mdMatch.index;
      if (cleanContent[startIdx] === '\\' || cleanContent[startIdx] === '"') {
        startIdx++;
      }
      
      const endMarkers = ['"quiz_json"', '"flashcards_json"', '"topic_name"', '"difficulty"', '","note_type"'];
      let endIdx = cleanContent.length;
      for (const marker of endMarkers) {
        const markerIdx = cleanContent.indexOf(marker, startIdx);
        if (markerIdx !== -1 && markerIdx < endIdx) {
          endIdx = markerIdx;
        }
      }
      
      let extracted = cleanContent.substring(startIdx, endIdx);
      extracted = extracted.replace(/\\?",?\s*$/, '');
      extracted = decodeEscaped(extracted);
      
      if (extracted.startsWith('#')) {
        return extracted;
      }
    }

    if (cleanContent.startsWith('#') || 
        cleanContent.startsWith('**') || 
        cleanContent.startsWith('-') ||
        cleanContent.startsWith('*') ||
        cleanContent.startsWith('1.') ||
        cleanContent.startsWith('>')) {
      return cleanContent;
    }

    if (!cleanContent.startsWith('{') && !cleanContent.startsWith('[')) {
      return cleanContent;
    }

    console.warn('[extractNoteText] Could not extract note_text from content');
    return '';
  };

  const fetchNote = async () => {
    try {
      const { data, error } = await supabase
        .from('notes')
        .select('id, title, content, created_at')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching note:', error);
        return;
      }

      if (data) {
        setNote(data);
        setEditedTitle(data.title || '');
        const extractedText = extractNoteText(data.content);
        setNoteText(extractedText);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTitle = () => {
    setEditedTitle(note?.title || '');
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim() || !note) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('notes')
        .update({ title: editedTitle.trim() })
        .eq('id', note.id);

      if (error) throw error;

      setNote({ ...note, title: editedTitle.trim() });
      setIsEditingTitle(false);
    } catch (error) {
      console.error('Error saving title:', error);
      showAlert('Erreur', 'Impossible de sauvegarder le titre');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedTitle(note?.title || '');
    setIsEditingTitle(false);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // ✅ AMÉLIORATION : Extraction du titre depuis le contenu Markdown
  const extractTitleFromContent = (markdown: string): string | null => {
    const h1Match = markdown.match(/^#\s+(.+)$/m);
    if (h1Match && h1Match[1]) {
      return h1Match[1].replace(/\*\*/g, '').trim();
    }
    return null;
  };

  // ✅ AMÉLIORATION : Conversion Markdown vers HTML plus robuste
  const markdownToHtml = (markdown: string): string => {
    let html = markdown;
    
    html = html.replace(/&/g, '&amp;');
    html = html.replace(/</g, '&lt;');
    html = html.replace(/>/g, '&gt;');
    
    html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');
    
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    
    html = html.replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>');
    
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    
    const lines = html.split('\n');
    let result: string[] = [];
    let inList = false;
    let listType: 'ul' | 'ol' | null = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const bulletMatch = line.match(/^[\-\*]\s+(.+)$/);
      const numberMatch = line.match(/^\d+\.\s+(.+)$/);
      
      if (bulletMatch) {
        if (!inList || listType !== 'ul') {
          if (inList) result.push(listType === 'ol' ? '</ol>' : '</ul>');
          result.push('<ul>');
          inList = true;
          listType = 'ul';
        }
        result.push(`<li>${bulletMatch[1]}</li>`);
      } else if (numberMatch) {
        if (!inList || listType !== 'ol') {
          if (inList) result.push(listType === 'ol' ? '</ol>' : '</ul>');
          result.push('<ol>');
          inList = true;
          listType = 'ol';
        }
        result.push(`<li>${numberMatch[1]}</li>`);
      } else {
        if (inList) {
          result.push(listType === 'ol' ? '</ol>' : '</ul>');
          inList = false;
          listType = null;
        }
        if (line.trim() && !line.startsWith('<h') && !line.startsWith('<blockquote')) {
          result.push(`<p>${line}</p>`);
        } else if (line.trim()) {
          result.push(line);
        }
      }
    }
    
    if (inList) {
      result.push(listType === 'ol' ? '</ol>' : '</ul>');
    }
    
    return result.join('\n');
  };

  // ✅ Export PDF accessible à TOUS (FREE et Premium)
  const handleDownloadPDF = async () => {
    if (!note || !noteText) return;
    
    setIsDownloading(true);
    try {
      const displayTitle = note.title && !note.title.startsWith('Fiche du') 
        ? note.title 
        : extractTitleFromContent(noteText) || note.title || 'Sans titre';

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            * { 
              box-sizing: border-box; 
              margin: 0;
              padding: 0;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              padding: 32px;
              max-width: 800px;
              margin: 0 auto;
              color: #0F172A;
              line-height: 1.5;
              font-size: 14px;
            }
            .header {
              background: linear-gradient(135deg, #6366F1, #7C3AED);
              color: white;
              padding: 24px;
              border-radius: 12px;
              margin-bottom: 20px;
            }
            .header-top {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 16px;
            }
            .logo {
              display: flex;
              align-items: center;
              gap: 8px;
              font-weight: 700;
              font-size: 16px;
            }
            .logo img {
              width: 28px;
              height: 28px;
              border-radius: 6px;
            }
            .badge {
              background: rgba(255,255,255,0.2);
              padding: 5px 12px;
              border-radius: 100px;
              font-size: 10px;
              font-weight: 700;
              letter-spacing: 1px;
            }
            .title {
              font-size: 22px;
              font-weight: 700;
              margin: 0 0 8px 0;
              line-height: 1.3;
            }
            .date {
              opacity: 0.8;
              font-size: 12px;
            }
            .content {
              background: white;
              padding: 24px;
              border-radius: 12px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.06);
            }
            h1 { 
              font-size: 18px; 
              color: #0F172A; 
              margin: 20px 0 10px 0;
              line-height: 1.3;
            }
            h1:first-child {
              margin-top: 0;
            }
            h2 { 
              font-size: 15px; 
              color: #6366F1; 
              margin: 18px 0 8px 0; 
              border-bottom: 2px solid #E0E7FF; 
              padding-bottom: 6px;
              line-height: 1.3;
            }
            h3 { 
              font-size: 14px; 
              color: #818CF8; 
              margin: 14px 0 6px 0;
              line-height: 1.3;
            }
            p {
              margin: 8px 0;
              line-height: 1.6;
            }
            ul, ol { 
              padding-left: 20px; 
              margin: 10px 0;
            }
            li { 
              margin: 4px 0;
              line-height: 1.5;
            }
            blockquote {
              background: #EEF2FF;
              border-left: 3px solid #6366F1;
              padding: 10px 14px;
              margin: 12px 0;
              border-radius: 6px;
            }
            code {
              background: #F1F5F9;
              padding: 1px 5px;
              border-radius: 3px;
              font-size: 12px;
            }
            strong {
              font-weight: 600;
              color: #0F172A;
            }
            .footer {
              margin-top: 20px;
              padding: 12px 16px;
              background: #F8FAFC;
              border-radius: 8px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 11px;
              color: #64748B;
            }
            .footer-left {
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .footer-left img {
              width: 16px;
              height: 16px;
              border-radius: 3px;
            }
            .footer-brand { 
              color: #6366F1; 
              font-weight: 600; 
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-top">
              <div class="logo">
                <img src="${ERNEST_LOGO_BASE64}" alt="Ernest" />
                Ernest
              </div>
              <div class="badge">FICHE</div>
            </div>
            <h1 class="title">${displayTitle}</h1>
            <div class="date">${formatDate(note.created_at)}</div>
          </div>
          <div class="content">
            ${markdownToHtml(noteText)}
          </div>
          <div class="footer">
            <div class="footer-left">
              <img src="${ERNEST_LOGO_BASE64}" alt="Ernest" />
              <span>Généré avec <span class="footer-brand">Ernest</span></span>
            </div>
            <span>ernest-app.fr</span>
          </div>
        </body>
        </html>
      `;

      const { uri } = await Print.printToFileAsync({
        html: htmlContent,
        base64: false,
      });

      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, {
          mimeType: 'application/pdf',
          dialogTitle: `Fiche - ${displayTitle}`,
          UTI: 'com.adobe.pdf',
        });
      } else {
        showAlert('Succès', 'PDF généré avec succès !');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      showAlert('Erreur', 'Impossible de générer le PDF. Réessaie.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={colors.indigo600} />
        <Text style={styles.loadingText}>Chargement de la fiche...</Text>
      </View>
    );
  }

  if (!note) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <View style={styles.container}>
          {/* ✅ Header FIXE pour l'état vide */}
          <View style={styles.headerWrapper}>
            <LinearGradient
              colors={[colors.indigo600, colors.purple600]}
              style={styles.headerFixed}
            >
              <View style={styles.headerFixedInner}>
                <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                  <ArrowLeft size={24} color={colors.white} weight="bold" />
                </TouchableOpacity>
                <Text style={styles.headerFixedTitle}>Fiche</Text>
                <View style={{ width: 40 }} />
              </View>
            </LinearGradient>
          </View>
          <View style={[styles.centered, { flex: 1 }]}>
            <FileText size={64} color={colors.slate400} weight="duotone" />
            <Text style={styles.emptyTitle}>Fiche introuvable</Text>
            <Text style={styles.emptySubtitle}>Cette fiche n'existe pas ou a été supprimée.</Text>
          </View>
        </View>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      
      <View style={styles.container}>
        {/* ✅ Header FIXE - En dehors du ScrollView */}
        <View style={styles.headerWrapper}>
          <LinearGradient
            colors={[colors.indigo600, colors.purple600]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.header}
          >
            <View style={styles.headerInner}>
              <View style={styles.headerTop}>
                <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
                  <ArrowLeft size={24} color={colors.white} weight="bold" />
                </TouchableOpacity>
                <View style={styles.logoContainer}>
                  <View style={styles.logoImageContainer}>
                    <Image 
                      source={require('../../assets/images/ernest.png')} 
                      style={styles.logoImage}
                    />
                  </View>
                  <Text style={styles.logoText}>Ernest</Text>
                </View>
                <View style={styles.typeBadge}>
                  <FileText size={14} color={colors.white} weight="bold" />
                  <Text style={styles.typeBadgeText}>FICHE</Text>
                </View>
              </View>

              {/* Title */}
              {isEditingTitle ? (
                <View style={styles.editTitleContainer}>
                  <TextInput
                    style={styles.editTitleInput}
                    value={editedTitle}
                    onChangeText={setEditedTitle}
                    placeholder="Titre de la fiche"
                    placeholderTextColor="rgba(255,255,255,0.5)"
                    autoFocus={!isWeb}
                    selectTextOnFocus
                  />
                  <View style={styles.editTitleActions}>
                    <TouchableOpacity style={styles.cancelButton} onPress={handleCancelEdit}>
                      <X size={20} color={colors.white} weight="bold" />
                    </TouchableOpacity>
                    <TouchableOpacity 
                      style={[styles.saveButton, !editedTitle.trim() && styles.saveButtonDisabled]} 
                      onPress={handleSaveTitle}
                      disabled={!editedTitle.trim() || isSaving}
                    >
                      {isSaving ? (
                        <ActivityIndicator size="small" color={colors.indigo600} />
                      ) : (
                        <Text style={styles.saveButtonText}>Enregistrer</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <TouchableOpacity onPress={handleEditTitle} style={styles.titleContainer}>
                  <Text style={styles.title} numberOfLines={2}>{note.title || 'Sans titre'}</Text>
                  <PencilSimple size={20} color="rgba(255,255,255,0.7)" weight="duotone" />
                </TouchableOpacity>
              )}

              {/* Meta */}
              <View style={styles.metaContainer}>
                <View style={styles.metaItem}>
                  <Text style={styles.metaText}>{formatDate(note.created_at)}</Text>
                </View>
              </View>
            </View>
          </LinearGradient>
        </View>

        {/* ✅ Contenu SCROLLABLE */}
        <ScrollView 
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
        >
          <View style={styles.innerContainer}>
            {/* Contenu de la fiche */}
            <View style={styles.contentContainer}>
              {noteText ? (
                <Markdown style={markdownStyles}>
                  {noteText}
                </Markdown>
              ) : (
                <View style={styles.emptyContent}>
                  <FileText size={48} color={colors.slate400} weight="duotone" />
                  <Text style={styles.emptyContentText}>
                    Le contenu de cette fiche n'est pas disponible.
                  </Text>
                </View>
              )}
            </View>

            {/* ✅ Bouton Télécharger PDF - Accessible à TOUS */}
            {noteText && (
              <View style={styles.downloadSection}>
                <TouchableOpacity
                  style={styles.downloadButton}
                  onPress={handleDownloadPDF}
                  disabled={isDownloading}
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={[colors.indigo600, colors.purple600]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.downloadButtonGradient}
                  >
                    {isDownloading ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <DownloadSimple size={20} color={colors.white} weight="bold" />
                    )}
                    <Text style={styles.downloadButtonText}>
                      {isDownloading ? 'Génération...' : 'Télécharger en PDF'}
                    </Text>
                  </LinearGradient>
                </TouchableOpacity>
              </View>
            )}

            {/* Footer signature */}
            <View style={styles.footer}>
              <View style={styles.footerLeft}>
                <Image 
                  source={require('../../assets/images/ernest.png')} 
                  style={styles.footerLogo}
                />
                <Text style={styles.footerText}>
                  Généré avec <Text style={styles.footerBrand}>Ernest</Text>
                </Text>
              </View>
              <Text style={styles.footerUrl}>ernest-app.fr</Text>
            </View>

            <View style={{ height: 40 }} />
          </View>
        </ScrollView>
      </View>
    </>
  );
}

const markdownStyles = {
  body: { 
    fontSize: 15, 
    lineHeight: 24, 
    color: colors.slate900,
  },
  strong: { 
    fontWeight: '700', 
    color: colors.slate900,
  },
  em: {
    fontStyle: 'italic',
  },
  bullet_list: { 
    marginVertical: 8,
  },
  ordered_list: { 
    marginVertical: 8,
  },
  list_item: { 
    marginVertical: 4,
  },
  paragraph: { 
    marginVertical: 8,
  },
  heading1: { 
    fontSize: 22, 
    fontWeight: '700', 
    color: colors.slate900, 
    marginTop: 20,
    marginBottom: 12,
    letterSpacing: -0.5,
  },
  heading2: { 
    fontSize: 18, 
    fontWeight: '700', 
    color: colors.indigo600, 
    marginTop: 24,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: colors.indigo100,
  },
  heading3: { 
    fontSize: 16, 
    fontWeight: '600', 
    color: colors.indigo500, 
    marginTop: 16,
    marginBottom: 8,
  },
  blockquote: { 
    backgroundColor: colors.indigo50, 
    borderLeftColor: colors.indigo600, 
    borderLeftWidth: 4, 
    paddingHorizontal: 16, 
    paddingVertical: 12, 
    marginVertical: 12,
    borderRadius: 8,
  },
  code_inline: { 
    backgroundColor: colors.slate100, 
    color: colors.indigo600, 
    paddingHorizontal: 6, 
    paddingVertical: 2, 
    borderRadius: 4, 
    fontSize: 13,
    fontFamily: 'monospace',
  },
  fence: { 
    backgroundColor: colors.slate100, 
    padding: 16, 
    borderRadius: 12, 
    marginVertical: 12,
  },
  hr: {
    backgroundColor: colors.slate200,
    height: 1,
    marginVertical: 20,
  },
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    color: colors.slate500,
    fontSize: 15,
  },

  // ✅ Header Wrapper - Fixe en haut
  headerWrapper: {
    backgroundColor: colors.slate50,
    width: '100%',
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  
  // Header Gradient
  header: {
    paddingTop: isWeb ? 24 : 60,
    paddingBottom: 24,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    ...(isWeb && {
      alignSelf: 'center',
    }),
  },
  headerInner: {
    width: '100%',
  },
  headerFixed: {
    paddingTop: isWeb ? 24 : 60,
    paddingBottom: 16,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    ...(isWeb && {
      alignSelf: 'center',
    }),
  },
  headerFixedInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerFixedTitle: {
    color: colors.white,
    fontSize: 20,
    fontWeight: '700',
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  logoImageContainer: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  logoImage: {
    width: 26,
    height: 26,
    resizeMode: 'contain',
  },
  logoText: {
    color: colors.white,
    fontSize: 18,
    fontWeight: '700',
  },
  typeBadge: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 100,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  typeBadgeText: {
    color: colors.white,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
    flex: 1,
    lineHeight: 30,
    letterSpacing: -0.5,
  },
  editTitleContainer: {
    marginBottom: 12,
  },
  editTitleInput: {
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.3)',
    borderRadius: 12,
    padding: 14,
    fontSize: 20,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 12,
  },
  editTitleActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  cancelButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  saveButton: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: colors.white,
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.indigo600,
  },
  metaContainer: {
    flexDirection: 'row',
    gap: 16,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.8)',
  },

  // ✅ ScrollView Content
  scrollContent: {
    paddingBottom: 20,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  innerContainer: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
  },

  // Content
  contentContainer: {
    backgroundColor: colors.white,
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 20,
    padding: 24,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 4,
  },
  emptyContent: {
    padding: 32,
    alignItems: 'center',
  },
  emptyContentText: {
    fontSize: 15,
    color: colors.slate500,
    textAlign: 'center',
    marginTop: 12,
  },

  // Download section
  downloadSection: {
    paddingHorizontal: 16,
    marginTop: 20,
  },
  downloadButton: {
    borderRadius: 16,
    overflow: 'hidden',
    ...(isWeb && {
      cursor: 'pointer',
    }),
  },
  downloadButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 16,
  },
  downloadButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '700',
  },

  // Footer
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: 16,
    padding: 16,
    backgroundColor: colors.white,
    borderRadius: 12,
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  footerLogo: {
    width: 24,
    height: 24,
    resizeMode: 'contain',
  },
  footerText: {
    fontSize: 13,
    color: colors.slate500,
  },
  footerBrand: {
    color: colors.indigo600,
    fontWeight: '600',
  },
  footerUrl: {
    fontSize: 12,
    color: colors.slate400,
  },

  // Empty state
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.slate900,
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 15,
    color: colors.slate500,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
});
